import { supabase } from '../client';
import { ApplicationStatusConflictError } from '../applicationStatusConflict';
import { computeSkillFitment } from '@core/candidates/skillFitment';
import { Application, ApplicationStatus, ApplicationStatusHistory, Job } from '@core/types';

export type UpdateApplicationStatusOptions = {
  /**
   * When set, the UPDATE runs only if `updated_at` still matches — detects concurrent edits (PRD §10.2).
   * Omit for legacy call sites; prefer passing from any UI that loaded the row.
   */
  expectedUpdatedAt?: string | null;
};

export type PipelineLockAcquireResult =
  | { ok: true; expiresSeconds?: number }
  | {
      ok: false;
      reason: string;
      lockedByDisplayName?: string;
      lockedByOrgUserId?: string;
    };

/**
 * Candidate embed: do not list source_channel here — many hosted DBs still lack it (PostgREST error
 * ggj_b2b_candidates_1.source_channel does not exist). Run supabase/migrations/015 on the project,
 * then add source_channel back inside ggj_b2b_candidates(...) below.
 */
const APPLICATION_LIST_SELECT_BASE = `
        *,
        candidate:ggj_b2b_candidates(id, full_name, email, phone, current_title, current_company, years_exp, location, base_resume_url),
        job:ggj_b2b_jobs(id, title, status, client_id),
        client:ggj_b2b_clients(id, name, contact_email, contact_name),
        assigned_user:ggj_b2b_org_users!assigned_to(id, display_name, email),
        pipeline_lock_holder:ggj_b2b_org_users!pipeline_lock_org_user_id(id, display_name, email)
      `;

const APPLICATION_LIST_SELECT_WITH_OPTIMIZATION = `${APPLICATION_LIST_SELECT_BASE.trim()},
        optimization:ggj_b2b_optimizations(ats_score_after, ats_score_before)
      `;

function normalizeApplicationEmbeds(r: Record<string, unknown>): Application {
  const rawOpt = r.optimization;
  let optimization: Application['optimization'] = null;
  if (Array.isArray(rawOpt)) optimization = (rawOpt[0] as Application['optimization']) ?? null;
  else if (rawOpt && typeof rawOpt === 'object') optimization = rawOpt as Application['optimization'];

  const rawPlh = r.pipeline_lock_holder;
  let pipeline_lock_holder: Application['pipeline_lock_holder'] = undefined;
  if (Array.isArray(rawPlh)) pipeline_lock_holder = (rawPlh[0] as Application['pipeline_lock_holder']) ?? null;
  else if (rawPlh && typeof rawPlh === 'object') pipeline_lock_holder = rawPlh as Application['pipeline_lock_holder'];

  return { ...r, optimization, pipeline_lock_holder } as Application;
}

function mapApplicationListRows(data: unknown[] | null): Application[] {
  return (data ?? []).map((row) => normalizeApplicationEmbeds(row as Record<string, unknown>));
}

type ListFilters = {
  candidateId?: string;
  jobId?: string;
  clientId?: string;
  assignedTo?: string;
  status?: ApplicationStatus;
  limit?: number;
};

function buildApplicationsListQuery(orgId: string, filters: ListFilters | undefined, select: string) {
  let query = supabase
    .from('ggj_b2b_applications')
    .select(select)
    .eq('org_id', orgId)
    .order('submitted_at', { ascending: false });

  if (filters?.candidateId) query = query.eq('candidate_id', filters.candidateId);
  if (filters?.jobId) query = query.eq('job_id', filters.jobId);
  if (filters?.clientId) query = query.eq('client_id', filters.clientId);
  if (filters?.assignedTo) query = query.eq('assigned_to', filters.assignedTo);
  if (filters?.status) query = query.eq('status', filters.status);
  if (filters?.limit != null) query = query.limit(Math.min(Math.max(1, filters.limit), 2000));

  return query;
}

export const applicationService = {
  /** MAT-030 / general: create a new application row */
  async create(payload: {
    org_id: string;
    candidate_id: string;
    job_id: string;
    client_id: string;
    created_by?: string | null;
    assigned_to?: string | null;
    internal_notes?: string | null;
    fitment_score?: number | null;
    fitment_json?: Record<string, unknown> | null;
  }): Promise<Application> {
    const insertRow: Record<string, unknown> = {
      org_id: payload.org_id,
      candidate_id: payload.candidate_id,
      job_id: payload.job_id,
      client_id: payload.client_id,
      status: 'submitted',
      fitment_score: payload.fitment_score ?? null,
      fitment_json: payload.fitment_json ?? null,
    };
    if (payload.created_by != null) insertRow.created_by = payload.created_by;
    if (payload.assigned_to !== undefined) insertRow.assigned_to = payload.assigned_to;
    if (payload.internal_notes !== undefined) insertRow.internal_notes = payload.internal_notes;

    const { data, error } = await supabase
      .from('ggj_b2b_applications')
      .insert(insertRow)
      .select(APPLICATION_LIST_SELECT_BASE)
      .single();
    if (error) throw error;
    return mapApplicationListRows([data])[0];
  },

  async findByCandidateAndJob(
    orgId: string,
    candidateId: string,
    jobId: string
  ): Promise<Application | null> {
    const apps = await this.list(orgId, { candidateId, jobId, limit: 1 });
    return apps[0] ?? null;
  },

  /**
   * Create application or refresh deterministic skill fitment on existing row (§9.5 / MAT-020).
   */
  async upsertApplicationWithSkillFitment(opts: {
    orgId: string;
    userId: string;
    candidateId: string;
    job: Pick<Job, 'id' | 'client_id' | 'skills_required'>;
    candidateSkillList: string[];
  }): Promise<Application> {
    const { fitment_score, fitment_json } = computeSkillFitment(
      opts.job.skills_required ?? [],
      opts.candidateSkillList
    );
    const existing = await this.findByCandidateAndJob(opts.orgId, opts.candidateId, opts.job.id);
    if (existing) {
      await this.persistFitment(existing.id, fitment_score, fitment_json);
      const up = await this.getById(existing.id);
      return up ?? existing;
    }
    return this.create({
      org_id: opts.orgId,
      candidate_id: opts.candidateId,
      job_id: opts.job.id,
      client_id: opts.job.client_id,
      created_by: opts.userId,
      fitment_score,
      fitment_json,
    });
  },

  async list(
    orgId: string,
    filters?: {
      candidateId?: string;
      jobId?: string;
      clientId?: string;
      assignedTo?: string;
      status?: ApplicationStatus;
      /** Cap rows (e.g. dashboard aggregates). Omit for full pipeline lists. */
      limit?: number;
    }
  ): Promise<Application[]> {
    // Optional: add recruiter_rating to candidate embed after migration 014 is applied on the project.
    let { data, error } = await buildApplicationsListQuery(
      orgId,
      filters,
      APPLICATION_LIST_SELECT_WITH_OPTIMIZATION
    );
    if (error) {
      console.warn(
        '[applicationService.list] Select with optimization embed failed; retrying without it:',
        error.message
      );
      ({ data, error } = await buildApplicationsListQuery(orgId, filters, APPLICATION_LIST_SELECT_BASE));
    }
    if (error) throw error;
    return mapApplicationListRows(data);
  },

  async getById(id: string): Promise<Application | null> {
    const { data } = await supabase
      .from('ggj_b2b_applications')
      .select(`
        *,
        candidate:ggj_b2b_candidates(*),
        job:ggj_b2b_jobs(*, client:ggj_b2b_clients(id, name)),
        client:ggj_b2b_clients(*),
        assigned_user:ggj_b2b_org_users!assigned_to(id, display_name, email, role),
        pipeline_lock_holder:ggj_b2b_org_users!pipeline_lock_org_user_id(id, display_name, email)
      `)
      .eq('id', id)
      .single();
    if (!data || typeof data !== 'object') return null;
    return normalizeApplicationEmbeds(data as Record<string, unknown>);
  },

  async acquirePipelineLock(
    applicationId: string,
    orgUserId: string,
    leaseSeconds = 120
  ): Promise<PipelineLockAcquireResult> {
    const { data, error } = await supabase.rpc('ggj_b2b_pipeline_lock_acquire', {
      p_application_id: applicationId,
      p_org_user_id: orgUserId,
      p_lease_seconds: leaseSeconds,
    });
    if (error) throw error;
    const j = (data ?? {}) as Record<string, unknown>;
    if (j.ok === true) {
      return {
        ok: true,
        expiresSeconds: j.expires_seconds != null ? Number(j.expires_seconds) : undefined,
      };
    }
    return {
      ok: false,
      reason: String(j.reason ?? 'locked'),
      lockedByDisplayName:
        j.locked_by_display_name != null ? String(j.locked_by_display_name) : undefined,
      lockedByOrgUserId:
        j.locked_by_org_user_id != null ? String(j.locked_by_org_user_id) : undefined,
    };
  },

  async releasePipelineLock(applicationId: string, orgUserId: string): Promise<void> {
    const { error } = await supabase.rpc('ggj_b2b_pipeline_lock_release', {
      p_application_id: applicationId,
      p_org_user_id: orgUserId,
    });
    if (error) throw error;
  },

  async updateStatus(
    id: string,
    status: ApplicationStatus,
    note?: string,
    rejectionReasonCode?: string | null,
    rejectionReasonText?: string | null,
    options?: UpdateApplicationStatusOptions,
  ): Promise<{ updated_at: string }> {
    const persistRejection = status === 'rejected' || status === 'offer_rejected';
    const expect = options?.expectedUpdatedAt;
    let q = supabase
      .from('ggj_b2b_applications')
      .update({
        status,
        ...(status === 'placed' ? { placement_date: new Date().toISOString().split('T')[0] } : {}),
        ...(persistRejection && rejectionReasonCode ? { rejection_reason_code: rejectionReasonCode } : {}),
        ...(persistRejection && rejectionReasonText ? { rejection_reason_text: rejectionReasonText } : {}),
      })
      .eq('id', id);
    if (expect != null && expect !== '') {
      q = q.eq('updated_at', expect);
    }
    const { data, error } = await q.select('updated_at').maybeSingle();
    if (error) throw error;
    if (expect != null && expect !== '') {
      if (data == null) {
        throw new ApplicationStatusConflictError(id);
      }
    } else if (data == null) {
      throw new Error('Application not found or could not be updated.');
    }

    if (note) {
      await supabase.from('ggj_b2b_application_status_history').insert({
        application_id: id,
        from_status: null,
        to_status: status,
        note,
      });
    }
    return { updated_at: data.updated_at };
  },

  /** CAN-030 / bulk: same status for many applications (org-scoped). */
  async bulkUpdateStatus(
    orgId: string,
    applicationIds: string[],
    status: ApplicationStatus,
    opts?: {
      rejectionReasonCode?: string | null;
      rejectionReasonText?: string | null;
      note?: string;
    }
  ): Promise<void> {
    if (!applicationIds.length) return;
    const persistRejection = status === 'rejected' || status === 'offer_rejected';
    const { error } = await supabase
      .from('ggj_b2b_applications')
      .update({
        status,
        ...(status === 'placed' ? { placement_date: new Date().toISOString().split('T')[0] } : {}),
        ...(persistRejection && opts?.rejectionReasonCode
          ? { rejection_reason_code: opts.rejectionReasonCode }
          : {}),
        ...(persistRejection && opts?.rejectionReasonText
          ? { rejection_reason_text: opts.rejectionReasonText }
          : {}),
      })
      .eq('org_id', orgId)
      .in('id', applicationIds);
    if (error) throw error;
    if (opts?.note) {
      await supabase.from('ggj_b2b_application_status_history').insert(
        applicationIds.map((application_id) => ({
          application_id,
          from_status: null,
          to_status: status,
          note: opts.note,
        }))
      );
    }
  },

  /** Export a list of applications to CSV string. */
  exportCsv(apps: import('@core/types').Application[]): string {
    const headers = ['Name', 'Email', 'Job', 'Client', 'Status', 'Submitted', 'Placed', 'Fee', 'Rejection Reason'];
    const rows = apps.map((a) => [
      a.candidate?.full_name ?? '',
      a.candidate?.email ?? '',
      a.job?.title ?? '',
      a.client?.name ?? '',
      a.status,
      a.submitted_at.split('T')[0],
      a.placement_date ?? '',
      a.placement_fee != null ? String(a.placement_fee) : '',
      a.rejection_reason_code ?? '',
    ]);
    return [headers, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\r\n');
  },

  async assign(id: string, assignedTo: string | null): Promise<void> {
    const { error } = await supabase
      .from('ggj_b2b_applications')
      .update({ assigned_to: assignedTo })
      .eq('id', id);
    if (error) throw error;
  },

  async updateNotes(id: string, notes: string): Promise<void> {
    const { error } = await supabase
      .from('ggj_b2b_applications')
      .update({ internal_notes: notes })
      .eq('id', id);
    if (error) throw error;
  },

  async updateOfferNotes(id: string, notes: string): Promise<void> {
    const { error } = await supabase
      .from('ggj_b2b_applications')
      .update({ offer_notes: notes })
      .eq('id', id);
    if (error) throw error;
  },

  async updatePlacement(id: string, fee: number, date: string): Promise<void> {
    const { error } = await supabase
      .from('ggj_b2b_applications')
      .update({ placement_fee: fee, placement_date: date })
      .eq('id', id);
    if (error) throw error;
  },

  async getStatusHistory(applicationId: string): Promise<ApplicationStatusHistory[]> {
    const { data, error } = await supabase
      .from('ggj_b2b_application_status_history')
      .select('*')
      .eq('application_id', applicationId)
      .order('changed_at', { ascending: true });
    if (error) throw error;
    return data ?? [];
  },

  async getPipelineStats(orgId: string): Promise<Record<ApplicationStatus, number>> {
    const { data, error } = await supabase
      .from('ggj_b2b_report_pipeline')
      .select('status, total')
      .eq('org_id', orgId);
    if (error) throw error;
    const stats: Partial<Record<ApplicationStatus, number>> = {};
    (data ?? []).forEach((row: { status: ApplicationStatus; total: number }) => {
      stats[row.status] = Number(row.total);
    });
    return stats as Record<ApplicationStatus, number>;
  },

  async getRecentPlacements(orgId: string, limit = 5) {
    const { data } = await supabase
      .from('ggj_b2b_report_placements')
      .select('candidate_name, job_title, client_name, placement_date, placement_fee')
      .eq('org_id', orgId)
      .order('placement_date', { ascending: false })
      .limit(limit);
    return data ?? [];
  },

  async getStaleApplications(orgId: string, daysOld = 7, rowLimit = 12) {
    const cutoff = new Date(Date.now() - daysOld * 86400000).toISOString();
    const { data } = await supabase
      .from('ggj_b2b_applications')
      .select('id, submitted_at, status, candidate:ggj_b2b_candidates(full_name), job:ggj_b2b_jobs(title), client:ggj_b2b_clients(name)')
      .eq('org_id', orgId)
      .in('status', ['submitted', 'viewed_by_client'])
      .lt('submitted_at', cutoff)
      .order('submitted_at', { ascending: true })
      .limit(Math.min(Math.max(1, rowLimit), 50));
    return (data ?? []) as Array<{
      id: string; submitted_at: string; status: string;
      candidate: { full_name: string } | null;
      job: { title: string } | null;
      client: { name: string } | null;
    }>;
  },

  async getRecentActivity(orgId: string, limit = 10) {
    const { data } = await supabase
      .from('ggj_b2b_application_status_history')
      .select(`
        id, to_status, changed_at, note,
        application:ggj_b2b_applications!application_id(
          id, org_id,
          candidate:ggj_b2b_candidates(full_name),
          job:ggj_b2b_jobs(title)
        )
      `)
      .order('changed_at', { ascending: false })
      .limit(limit * 3); // over-fetch, filter by org below

    const filtered = (data ?? []).filter(
      (r: { application: { org_id: string } | null }) => r.application?.org_id === orgId
    ).slice(0, limit);
    return filtered as Array<{
      id: string; to_status: string; changed_at: string; note: string | null;
      application: { id: string; candidate: { full_name: string } | null; job: { title: string } | null } | null;
    }>;
  },

  async getPendingQueries(orgId: string, limit = 5) {
    const { data } = await supabase
      .from('ggj_b2b_report_query_backlog')
      .select('thread_id, candidate_name, job_title, client_name, query_text, cta_responded_at, application_id')
      .eq('org_id', orgId)
      .order('cta_responded_at', { ascending: true })
      .limit(limit);
    return (data ?? []) as Array<{
      thread_id: string; candidate_name: string; job_title: string;
      client_name: string; query_text: string | null; cta_responded_at: string;
      application_id: string;
    }>;
  },

  /** MAT-020: persist fitment score + gap JSON on an application */
  async persistFitment(
    applicationId: string,
    fitmentScore: number,
    fitmentJson: Record<string, unknown>
  ): Promise<void> {
    const { error } = await supabase
      .from('ggj_b2b_applications')
      .update({ fitment_score: fitmentScore, fitment_json: fitmentJson })
      .eq('id', applicationId);
    if (error) throw error;
  },
};
